import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import useCreateTempRouteStore from "@/state/create-temp-route-store";
import AddressBoxDraggable from "../../address-box-component/address-box-draggable";

export interface CreateTempRouteSidebarSequenceProps {
  customHeader?: React.ReactNode;
}

/**
 * Sibling of RegionManagementSidebarSequence, reading from
 * create-temp-route-store instead. AddressBoxDraggable is pure/prop-driven
 * so it's reused as-is.
 */
const CreateTempRouteSidebarSequence: FC<
  CreateTempRouteSidebarSequenceProps
> = ({ customHeader }) => {
  const styles = useStyles();
  const { candidates, moveItem } = useCreateTempRouteStore();

  const sequencedAddresses = candidates
    .filter(
      (address) =>
        address.locationSequence !== null &&
        address.locationSequence !== undefined,
    )
    .sort(
      (a, b) => (a.locationSequence as number) - (b.locationSequence as number),
    );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sequencedAddresses.findIndex(
        (address) => address.id === active.id,
      );
      const newIndex = sequencedAddresses.findIndex(
        (address) => address.id === over?.id,
      );

      moveItem(oldIndex, newIndex);
    }
  };

  return (
    <Box sx={styles.sidebar}>
      {customHeader}
      {sequencedAddresses.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sequencedAddresses}
            strategy={verticalListSortingStrategy}
          >
            {sequencedAddresses.map((address, index) => (
              <AddressBoxDraggable
                {...address}
                key={address.id}
                index={index}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        <Box sx={styles.noSequenceView}>
          <Typography>Sequence will appear below.</Typography>
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  sidebar: {
    width: "324px",
    padding: "12px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    height: "calc(100vh - 150px)",
    overflow: "auto",
    marginTop: "24px",
  },
  noSequenceView: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "66vh",
  },
}));

export default CreateTempRouteSidebarSequence;
